/**
 * Stops Chrome from opening a file that was dragged from the desktop and dropped on a part of the
 * app that has no dropzone (#24688).
 *
 * Without this, the browser's default action for an unhandled file drop is to navigate to the file.
 * On a quote with unsaved changes that fires the `RoutePrompt` "Leave site?" dialog in the middle of
 * the drop, and on Linux Chrome the drag image then stays stuck to the cursor. On a clean quote the
 * user just loses the page.
 *
 * Only file drags are touched. Text and element drags (rich-text editors, react-sortable-hoc) keep
 * their default behaviour. React 18 dispatches events at the root container, so any dropzone that
 * accepts the drag (react-dropzone calls `preventDefault` and sets `dropEffect = 'copy'`) runs
 * before these window listeners, and the guard leaves it alone.
 */

type DragLikeEvent = Pick<DragEvent, 'dataTransfer' | 'defaultPrevented' | 'preventDefault'>

export function isFileDrag(event: Pick<DragEvent, 'dataTransfer'>): boolean {
  const types = event.dataTransfer?.types
  if (!types) {
    return false
  }
  return Array.from(types).includes('Files')
}

export function handleUnhandledFileDragOver(event: DragLikeEvent): void {
  if (!isFileDrag(event) || event.defaultPrevented) {
    return
  }
  // Cancelling dragover with a 'none' effect makes the browser reject the drop: the cursor shows
  // "no drop", no drop event fires, and the browser does not navigate to the file.
  event.preventDefault()
  if (event.dataTransfer) {
    event.dataTransfer.dropEffect = 'none'
  }
}

export function handleUnhandledFileDrop(event: DragLikeEvent): void {
  if (!isFileDrag(event) || event.defaultPrevented) {
    return
  }
  event.preventDefault()
}

/**
 * Installs the guard on `target` (the window by default). Returns a function that removes it.
 */
export function installUnhandledFileDropGuard(
  target: Pick<Window, 'addEventListener' | 'removeEventListener'> = window
): () => void {
  target.addEventListener('dragover', handleUnhandledFileDragOver)
  target.addEventListener('drop', handleUnhandledFileDrop)

  return () => {
    target.removeEventListener('dragover', handleUnhandledFileDragOver)
    target.removeEventListener('drop', handleUnhandledFileDrop)
  }
}
